import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { selectEmail, selectIsLoggedIn } from "../../Redux/authSlice";
import { orgApi, useGetMyMembershipQuery } from "../../Redux/orgApi";
import { mockMyMembership, mockOrg } from "./teamMockData";
import { useRerenderAt } from "./teamStatus";

// Preview the team views with mock data: add ?teamPreview=owner | admin | member
// to the URL. Nothing is remembered: without the parameter every page shows
// the user's real data. Only in development, or when REACT_APP_TEAM_PREVIEW=true.
const PREVIEW_ENABLED =
  process.env.NODE_ENV !== "production" || process.env.REACT_APP_TEAM_PREVIEW === "true";

const PREVIEW_ROLES = { owner: "Owner", admin: "Admin", member: "Member" };

// Owners and admins manage the team (Team page: invites, roles, removals).
// Billing (seats, card, invoices, cancelling) is the owner's alone.
// Members only use Pro; they never see the Team page or billing.
const withPermissions = (m) => ({
  ...m,
  canManage: m.role === "Owner" || m.role === "Admin",
  canBill: m.role === "Owner",
});

// The last /org/me answer, per account, so the sidebar and the team pages show
// straight away after signing in while the fresh answer loads. Only the
// server's answer decides access: the Team pages load their data from APIs
// that check the role.
const CACHE_PREFIX = "zt_team_me:";
const readCachedMembership = (email) => {
  if (!email) return undefined;
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + email);
    return raw ? JSON.parse(raw).membership ?? null : undefined;
  } catch {
    return undefined;
  }
};
const writeCachedMembership = (email, membership) => {
  try {
    localStorage.setItem(CACHE_PREFIX + email, JSON.stringify({ membership }));
  } catch {
    // Storage full or blocked: just no instant start next time.
  }
};
const clearCachedMemberships = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(CACHE_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Nothing to clear.
  }
};

/**
 * The signed-in user's team seat, from GET /api/org/me.
 * Returns { ready, membership, isPreview }: membership is null when the user
 * is not in a team (or the request failed); ready is false while it loads
 * for the first time on this device.
 */
export const useTeamAccess = () => {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const email = useSelector(selectEmail);
  const [searchParams] = useSearchParams();
  const preview = PREVIEW_ENABLED ? PREVIEW_ROLES[searchParams.get("teamPreview")] || null : null;

  const { data, isLoading, isUninitialized } = useGetMyMembershipQuery(undefined, {
    skip: !isLoggedIn || !!preview,
  });
  const cached = useMemo(() => (isLoggedIn ? readCachedMembership(email) : undefined), [isLoggedIn, email]);

  useEffect(() => {
    if (isLoggedIn && email && data) writeCachedMembership(email, data.membership ?? null);
  }, [isLoggedIn, email, data]);
  // Signing out forgets it. (Not "while signed out": every page load starts
  // signed out until the profile arrives.)
  const dispatch = useDispatch();
  const wasLoggedIn = useRef(isLoggedIn);
  useEffect(() => {
    if (wasLoggedIn.current && !isLoggedIn) {
      clearCachedMemberships();
      // Also the in-memory answers, so the next account never sees this one's team.
      dispatch(orgApi.util.invalidateTags(["OrgMe", "Org", "OrgBilling", "OrgInvites"]));
    }
    wasLoggedIn.current = isLoggedIn;
  }, [isLoggedIn, dispatch]);

  // The trial ending changes what the sidebar and pages show (Pro or not)
  // before any server event arrives: re-render at that moment.
  useRerenderAt([(data?.membership ?? cached)?.trialEndsAt]);

  return useMemo(() => {
    if (preview) {
      return {
        ready: true,
        isPreview: true,
        membership: withPermissions({
          orgId: mockOrg.id,
          orgName: mockOrg.name,
          orgStatus: mockOrg.status,
          role: preview,
          joinedAt: mockMyMembership.joinedAt,
          invitedByName: preview === "Owner" ? null : mockMyMembership.invitedBy,
          cancelAtPeriodEnd: false,
          currentPeriodEnd: mockOrg.currentPeriodEnd,
          isPreview: true,
        }),
      };
    }

    const loaded = !isLoading && !isUninitialized;
    const m = data ? data.membership : loaded ? null : cached;
    return {
      ready: !isLoggedIn || loaded || cached !== undefined,
      isPreview: false,
      membership: m ? withPermissions({ ...m, isPreview: false }) : null,
    };
  }, [preview, data, isLoading, isUninitialized, isLoggedIn, cached]);
};

const useTeamMembership = () => useTeamAccess().membership;

export default useTeamMembership;
